import React from 'react'
import { Box, makeStyles, Theme } from '@material-ui/core'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'

import { PipelineTab } from '../pipelineTabs'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        overflowX: 'auto',
        padding: theme.spacing(0.5),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.background.paper,
    },
    row: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.25),
        minWidth: 'max-content',
    },
    stageButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(0.75, 1.5),
        borderRadius: theme.spacing(0.75),
        border: 'none',
        cursor: 'pointer',
        fontSize: '0.875rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        backgroundColor: 'transparent',
        color: theme.palette.text.secondary,
        transition: 'background-color 0.15s ease, color 0.15s ease',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
            color: theme.palette.text.primary,
        },
    },
    stageButtonActive: {
        backgroundColor: theme.palette.primary.dark,
        color: theme.palette.common.white,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            color: theme.palette.common.white,
        },
    },
    stageButtonHasMatches: {
        boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
    },
    count: {
        borderRadius: 999,
        padding: '2px 6px',
        fontSize: '0.75rem',
        fontFamily: 'monospace',
        fontWeight: 600,
    },
    countActive: {
        backgroundColor: 'rgba(255,255,255,0.2)',
        color: theme.palette.common.white,
    },
    countInactive: {
        backgroundColor: theme.palette.action.selected,
        color: theme.palette.text.secondary,
    },
    countSearchMatch: {
        backgroundColor: theme.palette.warning.light,
        color: theme.palette.warning.dark,
    },
    countSearchMatchActive: {
        backgroundColor: 'rgba(255,255,255,0.35)',
        color: theme.palette.common.white,
    },
    chevron: {
        fontSize: 16,
        color: theme.palette.text.disabled,
    },
}))

interface StageSwitcherProps {
    tabs: PipelineTab[]
    activeTabId: string
    counts: Record<string, number>
    searchMatches?: Record<string, number> | null
    isSearchActive?: boolean
    onTabChange: (tabId: string) => void
}

export const StageSwitcher: React.FC<StageSwitcherProps> = ({
    tabs,
    activeTabId,
    counts,
    searchMatches,
    isSearchActive = false,
    onTabChange,
}) => {
    const classes = useStyles()

    return (
        <Box className={classes.root}>
            <Box className={classes.row}>
                {tabs.map((tab, i) => {
                    const matchCount =
                        isSearchActive && searchMatches
                            ? searchMatches[tab.id] ?? 0
                            : 0
                    const hasMatches = matchCount > 0
                    const displayCount = isSearchActive
                        ? matchCount
                        : counts[tab.id] ?? 0
                    const isActive = activeTabId === tab.id

                    let countClass = classes.countInactive
                    if (isActive) {
                        countClass =
                            hasMatches && isSearchActive
                                ? classes.countSearchMatchActive
                                : classes.countActive
                    } else if (hasMatches && isSearchActive) {
                        countClass = classes.countSearchMatch
                    }

                    return (
                        <React.Fragment key={tab.id}>
                            <button
                                type="button"
                                className={`${classes.stageButton} ${
                                    isActive ? classes.stageButtonActive : ''
                                } ${
                                    hasMatches && isSearchActive && !isActive
                                        ? classes.stageButtonHasMatches
                                        : ''
                                }`}
                                onClick={() => onTabChange(tab.id)}
                            >
                                {tab.label}
                                <span
                                    className={`${classes.count} ${countClass}`}
                                >
                                    {displayCount}
                                </span>
                            </button>
                            {i < tabs.length - 1 && (
                                <ChevronRightIcon className={classes.chevron} />
                            )}
                        </React.Fragment>
                    )
                })}
            </Box>
        </Box>
    )
}
